// Sortable + per-column filterable table helpers — ported from field-services
// (components/TableControls.jsx) so the CRM gets the same header filters George
// uses there. Self-contained: brand colors inlined (charcoal #1F2937, orange
// #E36F2C) so it doesn't depend on UI.jsx export keys.
//
// Usage:
//   const cols = { cat_class: r => r.cat_class, peak: r => r.peak, ... };
//   const { displayRows, headerProps, filters, clearFilter, clearAll } = useTableControls(rows, cols);
//   <thead><tr><SortableFilterTh {...headerProps('cat_class')} label="CAT-CLASS" /></tr></thead>
//   render displayRows (filtered + sorted) instead of raw rows.
//   <ActiveFiltersBar filters={filters} clearFilter={clearFilter} clearAll={clearAll} colLabels={{...}} />
//
// Optional third argument: sort accessors for columns whose filter text does
// not sort right (a date shown as MM/DD/YYYY, money shown as "$1,500,000").
// A column with no sort accessor sorts on its filter accessor, as before.

import { useState, useMemo, useCallback } from 'react';

const CHARCOAL = '#1F2937';
const ORANGE = '#E36F2C';
const MUTED = '#6B7280';

const TYPE_OPTS = [
  { value: 'contains',     label: '≈  Contains' },
  { value: 'begins',       label: '^  Begins with' },
  { value: 'ends',         label: '$  Ends with' },
  { value: 'equals',       label: '=  Equals' },
  { value: 'not_contains', label: '≠  Doesn\'t contain' },
  { value: 'not_equals',   label: '≠= Doesn\'t equal' },
];
export const TYPE_LABELS = Object.fromEntries(TYPE_OPTS.map(o => [o.value, o.label.trim()]));

export function useTableControls(rows, accessors, sortAccessors) {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [filters, setFilters] = useState({});

  const toggleSort = useCallback((col) => {
    setSortCol(prev => {
      if (prev === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); return col; }
      setSortDir('asc'); return col;
    });
  }, []);
  const setFilter = useCallback((col, type, value) => {
    setFilters(prev => ({ ...prev, [col]: { type, value } }));
  }, []);
  const clearFilter = useCallback((col) => {
    setFilters(prev => { const n = { ...prev }; delete n[col]; return n; });
  }, []);
  const clearAll = useCallback(() => { setFilters({}); setSortCol(null); }, []);

  const displayRows = useMemo(() => {
    let result = rows;
    for (const [col, { type, value }] of Object.entries(filters)) {
      if (!value) continue;
      const acc = accessors[col];
      if (!acc) continue;
      const v = value.toLowerCase();
      result = result.filter(row => {
        const cell = String(acc(row) ?? '').toLowerCase();
        switch (type) {
          case 'contains':     return cell.includes(v);
          case 'begins':       return cell.startsWith(v);
          case 'ends':         return cell.endsWith(v);
          case 'equals':       return cell === v;
          case 'not_contains': return !cell.includes(v);
          case 'not_equals':   return cell !== v;
          default:             return true;
        }
      });
    }
    if (sortCol && accessors[sortCol]) {
      const acc = (sortAccessors && sortAccessors[sortCol]) || accessors[sortCol];
      result = [...result].sort((a, b) => {
        const av = acc(a), bv = acc(b);
        // Numeric-aware compare; nulls sort last.
        const an = av == null || av === '' ? null : av;
        const bn = bv == null || bv === '' ? null : bv;
        if (an == null && bn == null) return 0;
        if (an == null) return 1;
        if (bn == null) return -1;
        const cmp = String(an).localeCompare(String(bn), undefined, { numeric: true, sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [rows, filters, sortCol, sortDir]);

  const headerProps = useCallback((col) => ({
    colKey: col, sortCol, sortDir, toggleSort, filters, setFilter, clearFilter,
  }), [sortCol, sortDir, toggleSort, filters, setFilter, clearFilter]);

  return { displayRows, sortCol, sortDir, toggleSort, filters, setFilter, clearFilter, clearAll, headerProps };
}

// Drop-in <th>: top zone = label + sort indicator (click to sort); bottom zone =
// [type▾] [filter input] [×]. noFilter hides the filter row (sort-only header).
//
// compact: for a table whose column widths are already measured tight (the
// Operations Active Jobs tables, 2026-09-28). The stock header adds width to
// every column: a nowrap label, 9px padding, the ⇅ glyph as its own flex item
// and a 26px type dropdown. Compact lets the label wrap, pads 6px like that
// table's cells, searches Contains only (no dropdown), puts the × inside the
// box, and shows the ▲/▼ only on the column being sorted (the label is still
// the click target, cursor and tooltip say so). A/B measured in a harness at
// 1300-1920: every column within 2px of the plain-label header, so typing or
// sorting never re-balances the table. Headers bottom-align so the search
// boxes line up when one label wraps to two lines; noFilter keeps an
// invisible box for the same reason. noSort (compact only) drops the
// click-to-sort for a column with nothing to sort, like a row's action buttons.
export const SortableFilterTh = ({
  label, colKey, sortCol, sortDir, toggleSort, filters, setFilter, clearFilter,
  align = 'left', noFilter = false, compact = false, noSort = false, style,
}) => {
  const isActive = sortCol === colKey;
  const filt = (filters && filters[colKey]) || { type: 'contains', value: '' };
  const hasFilter = !!filt.value;
  if (compact) {
    const sortable = !noSort && !!toggleSort;
    return (
      <th style={{ padding: 0, verticalAlign: 'bottom', textAlign: align, background: CHARCOAL, ...style }}>
        <div
          onClick={sortable ? () => toggleSort(colKey) : undefined}
          title={sortable ? `Sort by ${label}` : undefined}
          style={{
            padding: '8px 6px 6px', cursor: sortable ? 'pointer' : 'default', userSelect: 'none',
            borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: 11, fontWeight: 700, lineHeight: 1.3,
            letterSpacing: '0.04em', textTransform: 'uppercase', color: '#fff',
          }}
        >
          {label}
          {/* Sorted column only: an always-on ⇅ widened every label and cost
              In the Hunt's Site / Equipment 3-4px (measured). */}
          {sortable && isActive && <>{' '}<span style={{ fontSize: 10, color: '#fb923c' }}>
            {sortDir === 'asc' ? '▲' : '▼'}
          </span></>}
        </div>
        <div style={{ position: 'relative', padding: '4px 6px 5px', background: 'rgba(0,0,0,0.25)' }}>
          <input
            type="text"
            // size={1}, measured: a text box's natural width is ~20
            // characters, and on an auto-layout table that raises every
            // column's preferred width, so the free space spreads to the short
            // columns and Site / Equipment lose it (-35px at 1600). The box
            // still fills its column (width 100%); it just asks for nothing.
            size={1}
            value={noFilter ? '' : filt.value}
            readOnly={noFilter}
            tabIndex={noFilter ? -1 : undefined}
            aria-hidden={noFilter || undefined}
            aria-label={noFilter ? undefined : `Search ${label}`}
            title={noFilter ? undefined : `Search ${label} (contains)`}
            onChange={noFilter ? undefined : e => setFilter(colKey, filt.type, e.target.value)}
            placeholder={noFilter ? '' : '🔍'}
            style={{
              display: 'block', boxSizing: 'border-box', width: '100%', minWidth: 0, margin: 0,
              visibility: noFilter ? 'hidden' : 'visible',
              background: hasFilter ? 'rgba(251,146,60,0.25)' : 'rgba(255,255,255,0.1)',
              border: hasFilter ? '1px solid #fb923c' : '1px solid rgba(255,255,255,0.15)',
              borderRadius: 3, color: '#fff', fontSize: 11, outline: 'none',
              padding: hasFilter ? '2px 15px 2px 5px' : '2px 5px',
            }}
          />
          {hasFilter && !noFilter && (
            <button onClick={() => clearFilter(colKey)} title="Clear search"
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#fb923c', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 700, lineHeight: 1 }}>×</button>
          )}
        </div>
      </th>
    );
  }
  return (
    <th style={{ padding: 0, verticalAlign: 'top', whiteSpace: 'nowrap', background: CHARCOAL, ...style }}>
      <div
        onClick={() => toggleSort(colKey)}
        title={`Sort by ${label}`}
        style={{
          padding: '7px 9px', cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 5,
          justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
          borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.04em', textTransform: 'uppercase', color: '#fff',
        }}
      >
        <span>{label}</span>
        <span style={{ fontSize: 10, opacity: isActive ? 1 : 0.3, color: isActive ? '#fb923c' : '#fff', fontWeight: 700, lineHeight: 1, flexShrink: 0 }}>
          {isActive ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </div>
      {!noFilter && (
        <div style={{ padding: '4px 6px', background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 3 }}>
          <select
            value={filt.type}
            onChange={e => setFilter(colKey, e.target.value, filt.value)}
            onClick={e => e.stopPropagation()}
            title="Filter type"
            style={{ background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none', borderRadius: 3, fontSize: 11, padding: '2px 1px', cursor: 'pointer', width: 26, flexShrink: 0, outline: 'none', appearance: 'none', WebkitAppearance: 'none', textAlign: 'center' }}
          >
            {TYPE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <input
            type="text" value={filt.value}
            onChange={e => setFilter(colKey, filt.type, e.target.value)}
            onClick={e => e.stopPropagation()}
            placeholder="Filter…"
            style={{ flex: 1, minWidth: 0, background: hasFilter ? 'rgba(251,146,60,0.25)' : 'rgba(255,255,255,0.1)', border: hasFilter ? '1px solid #fb923c' : '1px solid rgba(255,255,255,0.15)', borderRadius: 3, color: '#fff', fontSize: 11, padding: '2px 5px', outline: 'none', width: '100%' }}
          />
          {hasFilter && (
            <button onClick={e => { e.stopPropagation(); clearFilter(colKey); }} title="Clear filter"
              style={{ background: 'none', border: 'none', color: '#fb923c', cursor: 'pointer', padding: '0 2px', fontSize: 14, fontWeight: 700, lineHeight: 1, flexShrink: 0 }}>×</button>
          )}
        </div>
      )}
    </th>
  );
};

export const ActiveFiltersBar = ({ filters, clearFilter, clearAll, colLabels = {} }) => {
  const active = Object.entries(filters || {}).filter(([, f]) => f.value);
  if (!active.length) return null;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8, fontSize: 11 }}>
      <span style={{ color: MUTED, fontWeight: 600 }}>Active filters:</span>
      {active.map(([col, { type, value }]) => (
        <span key={col} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff7ed', border: `1px solid ${ORANGE}`, borderRadius: 12, padding: '2px 10px', color: CHARCOAL }}>
          <strong>{colLabels[col] || col}</strong>
          <span style={{ color: MUTED }}>{TYPE_LABELS[type]}</span>
          <span>"{value}"</span>
          <button onClick={() => clearFilter(col)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: ORANGE, padding: '0 1px', fontWeight: 700, fontSize: 13, lineHeight: 1 }}>×</button>
        </span>
      ))}
      <button onClick={clearAll} style={{ background: 'none', border: `1px solid ${MUTED}`, borderRadius: 4, color: MUTED, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}>Clear all</button>
    </div>
  );
};
